import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './redux';
import { setCredentials, setUser, logout as logoutAction, setLoading, setError } from '../store/slices/authSlice';
import authService from '../services/auth.service';
import { 
  User,
  LoginCredentials, 
  RegisterData, 
  ChangePassword,
  PasswordResetRequest,
  PasswordResetConfirm 
} from '../types/auth.types';

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, error } = useAppSelector((state) => state.auth);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (authService.isAuthenticated() && !user) {
        dispatch(setLoading(true));
        try {
          const userData = await authService.getCurrentUser();
          dispatch(setUser(userData));
        } catch (error) {
          // If token is invalid, clear it and logout
          dispatch(logoutAction());
        } finally {
          dispatch(setLoading(false));
        }
      } else if (!authService.isAuthenticated()) {
        // If no token, ensure loading is false
        dispatch(setLoading(false));
      }
    };

    checkAuth();
  }, [dispatch, user]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    try {
      const response = await authService.login(credentials);
      dispatch(setCredentials({
        user: response.user,
        access: response.access,
        refresh: response.refresh,
      }));
      navigate('/dashboard');
      return response;
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Login failed';
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, navigate]);

  const register = useCallback(async (data: RegisterData) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    try {
      const response = await authService.register(data);
      dispatch(setCredentials({
        user: response.user,
        access: response.access,
        refresh: response.refresh,
      }));
      navigate('/dashboard');
      return response;
    } catch (error: any) {
      const message = error.response?.data?.detail || 
                     error.response?.data?.email?.[0] || 
                     error.response?.data?.password?.[0] || 
                     'Registration failed';
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, navigate]);

  const logout = useCallback(async () => {
    dispatch(setLoading(true));
    
    try {
      await authService.logout();
    } finally {
      dispatch(logoutAction());
      navigate('/login');
    }
  }, [dispatch, navigate]);

  const updateProfile = useCallback(async (data: Partial<User>) => {
    if (!user) return;
    
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    try {
      const updatedUser = await authService.updateProfile(data);
      dispatch(setUser(updatedUser));
      return updatedUser;
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Profile update failed';
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, user]);

  const changePassword = useCallback(async (data: ChangePassword) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    try {
      await authService.changePassword(data);
      return true;
    } catch (error: any) {
      const message = error.response?.data?.detail || 
                     error.response?.data?.old_password?.[0] || 
                     error.response?.data?.new_password?.[0] || 
                     'Password change failed';
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const requestPasswordReset = useCallback(async (data: PasswordResetRequest) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    try {
      await authService.requestPasswordReset(data);
      return true;
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Password reset request failed';
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const confirmPasswordReset = useCallback(async (data: PasswordResetConfirm) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    try {
      await authService.confirmPasswordReset(data);
      // Let the form show its success panel (with a "Back to sign in" link)
      // rather than redirecting straight to /login, which would unmount the
      // form before that confirmation could render.
      return true;
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Password reset failed';
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    requestPasswordReset,
    confirmPasswordReset,
  };
};